import apiClient from "../../api/api";

/**
 * Retrieves a paginated list of managers.
 *
 * @async
 * @function requestGetManagers
 * @param {number} page - Page number for pagination (starting at 1).
 * @param {number} limit - Maximum number of managers per page.
 * @returns {Promise<Object>} Paginated list of managers returned by the API.
 *
 * @example
 * await requestGetManagers(1, 25);
 */
export const requestGetManagers = async(page, limit) => {
    const params = {
        page: page,
        limit
    };
    const getManagersResponse = await apiClient.get(import.meta.env.VITE_BASE_MANAGER_URL, { params })
    return getManagersResponse.data
}

/**
 * Retrieves a paginated list of team members associated with a specific manager.
 *
 * Supports optional filtering by user name and inclusion of disabled users.
 *
 * @async
 * @function getTeamMembers
 * @param {number|string} managerId - Unique identifier of the manager.
 * @param {number} page - Page number for pagination (starting at 1).
 * @param {number} limit - Maximum number of records per page.
 * @param {boolean} canShowDisabled - Indicates whether disabled team members should be included.
 * @param {string|null|undefined} searchString - Optional user name or partial name used for filtering.
 * @returns {Promise<Object>} Paginated list of team members returned by the API.
 *
 * @example
 * // Get first page of active team members
 * await getTeamMembers(12, 1, 25, false);
 *
 * @example
 * // Search team members by name, including disabled users
 * await getTeamMembers(1179, true, "Ana", 1, 25);
 */
export const getTeamMembers = async(managerId, canShowDisabled, searchString, page = 1, limit = 1000) => {
    const params = {
        page,
        limit,
        isDisabled: canShowDisabled,
    };

     if (searchString && searchString.trim() !== "") {
        params.search = searchString;
    }

    const getTeamMembersReponse = await apiClient.get(
        import.meta.env.VITE_BASE_MANAGER_URL+`/${managerId}/team-members`,
        { params }
    );

    return getTeamMembersReponse.data
}

/**
 * Retrieves all pending manual time requests assigned to a specific manager.
 *
 * @async
 * @function getPendingManualTimeByManagerRequest
 * @param {number|string} managerId - Unique identifier of the manager.
 * @param {string} month - Selected month.
 * @param {string} year - Selected year.
 * @returns {Promise<Array<Object>>} List of pending manual time requests.
 *
 * @example
 * await getPendingManualTimeByManagerRequest(3);
 */
export const getPendingRequestsByManagerRequest = async(type, month, year) => {
    let params = {}
    if(type){
        params = {
            type,
            month,
            year
        }
    }
    const getManualTimeResponse = await apiClient.get(import.meta.env.VITE_BASE_REQUEST_URL+`/pending`, { params, timeout: 90000 })
    
    return getManualTimeResponse.data
}